import React from 'react';

import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { axiosClient } from 'src/utils/axiosClient';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { GENERIC_ERROR_MESSAGE, RegexPatterns } from 'src/utils/constants';

interface FormStatus {
  status: 'unsubmitted' | 'error' | 'success';
  helperText?: string;
}

const ProspectSignupForm: React.FC<{ name: string }> = ({ name }) => {
  const {
    publicRuntimeConfig: { apiUrls, frontendUrls },
  } = getNextConfigValues();

  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);

  const [formStatus, setFormStatus] = React.useState<FormStatus>({
    status: 'unsubmitted',
  });

  const theme = useTheme();
  const user = useNonNullContext(UserContext);

  const handleSubmit = async () => {
    // shortcut and set validation error state if email pattern does not match
    if (!RegexPatterns.EMAIL_REGEX.test(email)) {
      setFormStatus({
        status: 'error',
        helperText: 'Please enter a valid email',
      });
      return;
    }
    const url = `${apiUrls.railsCheckoutV1}/prospects`;
    try {
      await axiosClient.post(
        url,
        {
          tenant_id: user.tenantId,
          email: email,
          name,
          source: 'ecommerce-platform-get-started', // @todo is this correct? modded from 'prospect_homepage_footer' value
          marketing_optin: 'EXPLICITLY_OPTEDIN',
        },
        { withCredentials: true },
      );
      setFormStatus({
        status: 'success',
        helperText: 'Subscribed!',
      });
      setTimeout(() => {
        // route to app root after brief pause on success.
        // use window.location.href rather than next router b/c this will be served by a different application
        // (at least at time of writing; this homepage is served by Rails.)
        window.location.href = frontendUrls.homepage;
      }, 500);
    } catch (err: any) {
      const responseJson = await err?.response?.json();
      const emailValidationErrors = responseJson?.error?.validation?.email;
      const emailTaken =
        emailValidationErrors &&
        emailValidationErrors.length &&
        emailValidationErrors[0] === 'has already been taken';
      if (emailTaken) {
        setFormStatus({
          status: 'error',
          helperText: 'You are already signed up!',
        });
      } else {
        setFormStatus({
          status: 'error',
          helperText: GENERIC_ERROR_MESSAGE,
        });
        logger.error({ noticable: err, msg: 'failed to subscribe email' });
      }
    }
  };

  return (
    <Card
      sx={{
        marginBottom: '2rem',
        // @todo background color from figma
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ flexDirection: 'column', padding: '2rem' }}
      >
        <Grid>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.main }}
          >
            Unfortunately we don&apos;t deliver in your area yet! Leave your
            email address and we&apos;ll notify you as soon as we&apos;re
            available for you!
          </Typography>
        </Grid>
        <Grid>
          <TextField
            fullWidth
            color={formStatus.status === 'success' ? 'success' : 'primary'}
            placeholder="Your Email"
            error={formStatus.status === 'error'}
            helperText={formStatus.helperText}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.trim());
            }}
            slotProps={{
              formHelperText: {
                style:
                  formStatus.status === 'success'
                    ? {
                        color: theme.palette.success.light,
                        textAlign: 'center',
                      }
                    : { textAlign: 'center' },
              },
            }}
          />
        </Grid>
        <Grid>
          {/* @todo designs should have Tenant name like "Territory" in copy */}
          <FormControlLabel
            control={
              <Checkbox
                value={checked}
                onChange={() => {
                  setChecked(!checked);
                }}
              />
            }
            label="I accept the Terms and Services"
          />
        </Grid>
        <Grid>
          <Button
            variant="contained"
            fullWidth
            disabled={!email || !checked}
            onClick={() => {
              handleSubmit();
            }}
          >
            Notify Me
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export default ProspectSignupForm;
